import React from 'react';
import {useIntl} from 'react-intl';
import classNames from 'classnames';

import {FileTypes} from 'utils/constants';
import {getFileType, isGIFImage} from 'utils/utils';
import {type MentionKey, type TextFormattingOptions} from 'utils/text_formatting';

import {General} from 'mattermost-redux/constants';
import {type Post} from 'mattermost-redux/types/posts';
import {type FileInfo} from 'mattermost-redux/types/files';
import {getFileUrl, getFilePreviewUrl} from 'mattermost-redux/utils/file_utils';

import PostMarkdown from 'components/post_markdown';
import FileThumbnail from 'components/file_attachment/file_thumbnail';
import PostAttachmentContainer from 'components/post_view/post_attachment_container/post_attachment_container';

import {Renderer} from '@time-webkit/marked';
import {CloseIcon} from '@time-webkit/all/icons';
import {Avatar} from '@time-webkit/all/atoms/avatar';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {PostQuoteMarkdown} from './post_quote_markdown';

import styles from './style.module.css';

export type Props = {
    currentTeamUrl: string;
    overrideUsername?: string;
    post: Post;
    channelType: string;
    teamName: string;
    fileInfos: FileInfo[];
    userDisplayName?: string;
    isPreview?: boolean;
    onClose?: () => void;
    isEdit?: boolean;
    isAdditionalContent?: boolean;
};

const createRenderer = (
    textFormattingOptions: TextFormattingOptions,
) => new PostQuoteMarkdown(new Renderer(), textFormattingOptions);

const markdownOptions: TextFormattingOptions = {
    singleline: true,
    disableAutolinkEmails: true,
    createRenderer,
};

const markdownMentionKeys: MentionKey[] = [];

export const PostQuote = (props: Props) => {
    const {currentTeamUrl, post, isPreview, channelType, teamName, fileInfos, onClose, isAdditionalContent = false} = props;
    const {formatMessage} = useIntl();

    const [bodyM, bodyMTrue] = useTypography([
        {size: TypographySize.BodyM},
        {size: TypographySize.BodyMTrue},
    ]);

    let attachment = null;

    if (fileInfos[0]) {
        const file = fileInfos[0];
        const fileType = getFileType(file.extension);

        const isImageWithPreview = fileType === FileTypes.IMAGE && file.has_preview_image;
        const isGIF = isGIFImage(file.extension);

        attachment = (isImageWithPreview || isGIF) ? (
            <div className='quote-avatar'>
                <Avatar
                    size={32}
                    src={isGIF ? getFileUrl(file.id) : getFilePreviewUrl(file.id)}
                    className={classNames('avatar-post-preview', styles.avatar)}
                />
            </div>
        ) : (
            <div className='quote-thumbnail'>
                <FileThumbnail fileInfo={file} />
            </div>
        );
    }

    const teamUrl = (channelType === General.DM_CHANNEL || channelType === General.GM_CHANNEL) ? currentTeamUrl : `/${teamName}`;
    const userName = props.isEdit ? '' : props.overrideUsername || props.userDisplayName || '';
    const text = props.isEdit ? formatMessage({id: 'post_quote.edit_message', defaultMessage: 'Edit message'}) : formatMessage({id: 'post_quote.replyTo', defaultMessage: 'Reply to'});
    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation();

        onClose?.();
    };

    let message = post.message;

    if (!message && fileInfos.length) {
        message = formatMessage({
            id: 'post_quote.attachments',
            defaultMessage: '{count, plural, one {attachment} other {attachments}}',
        }, {
            count: fileInfos.length,
        });
    }

    return (
        <PostAttachmentContainer
            className={classNames('quote', {'quote-preview': isPreview})}
            link={`${teamUrl}/pl/${post.id}`}
            ignoreNestedPreviews={true}
        >
            <div className='quote-content'>
                <div className={classNames(styles.vertical, {[styles.leftIndent]: isAdditionalContent})}/>
                {attachment}
                <div className='quote-message'>
                    <div className={classNames(styles.header, bodyMTrue)}>
                        {isPreview && `${text} `}{userName}
                    </div>
                    <div
                        aria-readonly='true'
                        tabIndex={0}
                        className={classNames('quote-message__text', bodyM)}
                        dir='auto'
                        data-qa='quote_message_text'
                    >
                        <PostMarkdown
                            message={message}
                            post={post}
                            channelId={post.channel_id}
                            mentionKeys={markdownMentionKeys}
                            options={markdownOptions}
                        />
                    </div>
                </div>
                {isPreview && (
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        aria-label='Close'
                        style={{boxShadow: 'none'}}
                    >
                        <div className={styles.iconWrapper}>
                            <CloseIcon width={16} height={16} />
                        </div>
                    </button>

                )}
            </div>
        </PostAttachmentContainer>
    );
};
